import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { DataGrid, GridColDef, Modal, useLocalStorageAsState } from "@msys/ui";
import {
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Replay as ReplayIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import * as uuid from "uuid";
import * as Yup from "yup";
import { DatanormOnlineRecommendationStatus } from "../../../clients/graphqlTypes.js";
import { PasswordField } from "../../commons/form-fields/PasswordField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import {
  DatanormOnlineEntriesDocument,
  DatanormOnlineEntriesQuery,
  DatanormOnlineEntriesQueryVariables,
  DatanormOnlineEntryFragment,
  useImportDatanormOnlineEntriesMutation,
  useImportDatanormOnlineEntryMutation,
} from "./ImportViaDatanormOnlineModal.generated.js";

// The integration targets DATANORM Online 5.0; the availability/info payload
// does not carry a version, so it is surfaced as a constant.
const DATANORM_ONLINE_VERSION = "5.0";

const getRowId = (row: DatanormOnlineEntryFragment) =>
  `${row.url}-${row.filename}-${row.date}-${row.dataDate}`;

// Deliberate duplicate of `parseGermanDate` in
// libs/itek/companies/src/lib/utils/germanDate.ts — that lib is backend-only
// and cannot be imported here. It is the canonical parser: keep both the regex
// and the calendar-validity check in step with it, so an entry the backend
// treats as unparseable sorts last here too, as the spec requires.
const GERMAN_DATE_RE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

function germanDateToSortKey(s: string | null | undefined): string {
  if (!s) return "";
  const m = GERMAN_DATE_RE.exec(s);
  if (!m) return "";

  const [, dayStr, monthStr, yearStr] = m;
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  // reject calendar-invalid dates (e.g. "31.02.2026") via the same `getUTC*`
  // round trip the backend parser uses — an empty key sorts last under the
  // descending comparison below.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return `${yearStr}-${monthStr}-${dayStr}`;
}

function sortEntries(
  entries: DatanormOnlineEntryFragment[]
): DatanormOnlineEntryFragment[] {
  return [...entries].sort((a, b) => {
    const aKey = germanDateToSortKey(a.date);
    const bKey = germanDateToSortKey(b.date);
    if (aKey !== bKey) return bKey.localeCompare(aKey);
    return a.description.localeCompare(b.description);
  });
}

interface FormValues {
  clientId: string;
  username: string;
  password: string;
}

interface Props {
  supplierId: string;
  itekCompanyId: string;
  datanormOnlineInfo: {
    clientIdRequired: boolean;
    usernameRequired: boolean;
    passwordRequired: boolean;
  };
  handleClose: () => void;
  refetchQueries?: string[];
}

export function ImportViaDatanormOnlineModal({
  supplierId,
  itekCompanyId,
  datanormOnlineInfo,
  handleClose,
  refetchQueries,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["PurchaseOrders", "PimImportations", "Global"]);

  const [entries, setEntries] = React.useState<
    DatanormOnlineEntryFragment[] | null
  >(null);

  // The account the entries were fetched for, surfaced in the connection-active
  // header once a connection succeeds.
  const [connectedAccount, setConnectedAccount] = React.useState<{
    clientId: string;
    username: string;
  } | null>(null);

  const [statuses, setStatuses] = React.useState<
    Record<string, "loading" | "error" | "done">
  >({});

  const [moreExpanded, setMoreExpanded] = React.useState(false);

  const client = useApolloClient();

  // Only the caller's queries are refetched declaratively. The entries query
  // cannot be: it is issued imperatively via `client.query` (see
  // `fetchEntries`), so Apollo holds no active observable query under that name
  // for `refetchQueries` to match, and the grids render from `entries` state
  // rather than from the cache. Both imports therefore call `refreshEntries`
  // explicitly on success.
  const [addPimImport] = useImportDatanormOnlineEntryMutation({
    client,
    refetchQueries,
  });
  const [addPimImportBatch] = useImportDatanormOnlineEntriesMutation({
    client,
    refetchQueries,
  });

  // multi-select state for the batch "Sync" action
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  const [isSyncing, setIsSyncing] = React.useState(false);
  // coarse, batch-level error banner (no per-row error states for the batch flow)
  const [syncFailed, setSyncFailed] = React.useState(false);
  // snapshot of the row ids from the last attempted batch, so "Retry Sync"
  // reissues exactly that set even if the grid selection changes meanwhile
  const [failedBatchRowIds, setFailedBatchRowIds] = React.useState<string[]>(
    []
  );

  const [datanormOnlineLogin, setDatanormOnlineLogin] = useLocalStorageAsState<{
    clientId: string;
    username: string;
  }>(`msys-datanorm-online-login-${itekCompanyId}`, {
    clientId: "",
    username: "",
  });

  const initialValues: FormValues = {
    clientId: datanormOnlineLogin?.clientId ?? "",
    username: datanormOnlineLogin?.username ?? "",
    password: "",
  };

  const validationSchema = Yup.object().shape({
    ...(datanormOnlineInfo.clientIdRequired
      ? {
          clientId: Yup.string()
            .label(
              t("Client id", {
                ns: "PurchaseOrders",
              })
            )
            .required(),
        }
      : undefined),
    ...(datanormOnlineInfo.usernameRequired
      ? {
          username: Yup.string()
            .label(
              t("Username", {
                ns: "PurchaseOrders",
              })
            )
            .required(),
        }
      : undefined),
    ...(datanormOnlineInfo.passwordRequired
      ? {
          password: Yup.string()
            .label(
              t("Password", {
                ns: "PurchaseOrders",
              })
            )
            .required(),
        }
      : undefined),
  });

  const formId = React.useMemo(() => uuid.v4(), []);

  const fetchEntries = async ({ username, clientId, password }: FormValues) => {
    const { data } = await client.query<
      DatanormOnlineEntriesQuery,
      DatanormOnlineEntriesQueryVariables
    >({
      query: DatanormOnlineEntriesDocument,
      variables: {
        authentication: {
          itekCompanyId,
          username,
          clientId,
          password,
        },
        pimSupplierId: supplierId,
      },
      // The Recommended/More split is derived server-side from the supplier's
      // importation history, so a cached result is stale the moment an import
      // succeeds — `refreshEntries` re-runs this and must hit the network.
      fetchPolicy: "network-only",
    });
    const result = data?.datanormOnlineEntries;
    if (result?.__typename === "MissingPermissions") {
      throw new Error("You don't have permission to access these catalogues.");
    }
    const entries = getDataOrNull(result)?.entries;
    // A null result means the connection itself failed (surfaced by the
    // callers). An empty list is a successful connection without entitled
    // catalogues and is handled as a dedicated empty-state, not an error.
    if (!entries) throw new Error("No entries found");
    return entries;
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setEntries(await fetchEntries(values));
      setConnectedAccount({
        clientId: values.clientId,
        username: values.username,
      });
      setDatanormOnlineLogin({
        username: values.username,
        clientId: values.clientId,
      });
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  /**
   * Re-read the entries after a successful import. `isRecommended` and the
   * NEW/UPDATE/REIMPORT chips are computed from the supplier's importation
   * history, so a just-imported entry only leaves "Recommended" once the list
   * is fetched again.
   */
  const refreshEntries = async (values: FormValues) => {
    try {
      setEntries(await fetchEntries(values));
    } catch (e) {
      // A failed refresh must not read as a failed import — the importation was
      // already created. Keep the list as it stands and surface the error.
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const handleImport = async (
    values: FormValues,
    row: DatanormOnlineEntryFragment
  ) => {
    const { username, clientId, password } = values;
    setStatuses(s => ({ ...s, [getRowId(row)]: "loading" }));
    try {
      if (!row.url) {
        throw new Error("Url not found");
      }
      await addPimImport({
        variables: {
          input: {
            authentication: {
              itekCompanyId,
              username,
              clientId,
              password,
            },
            pimSupplierId: supplierId,
            datanormOnlineEntry: {
              description: row.description,
              dataDate: row.dataDate,
              date: row.date,
              filename: row.filename,
              url: row.url,
            },
          },
        },
      });
      setStatuses(s => ({ ...s, [getRowId(row)]: "done" }));
      await refreshEntries(values);
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
      setStatuses(s => ({ ...s, [getRowId(row)]: "error" }));
    }
  };

  const renderStatusChip = (
    status: DatanormOnlineRecommendationStatus | null | undefined
  ) => {
    if (status === "NEW") {
      return (
        <Chip
          label={t("New", { ns: "PimImportations" })}
          size="small"
          variant="filled"
          color="success"
          sx={{ textTransform: "uppercase" }}
        />
      );
    }
    if (status === "UPDATE") {
      return (
        <Chip
          label={t("Update", { ns: "PimImportations" })}
          size="small"
          variant="filled"
          color="warning"
          sx={{ textTransform: "uppercase" }}
        />
      );
    }
    if (status === "REIMPORT") {
      return (
        <Chip
          label={t("Reimport", { ns: "PimImportations" })}
          size="small"
          variant="filled"
          color="info"
          sx={{ textTransform: "uppercase" }}
        />
      );
    }
    return null;
  };

  const buildColumns = (
    formikProps: { values: FormValues },
    options: { includeStatus: boolean }
  ): GridColDef<DatanormOnlineEntryFragment>[] => [
    {
      field: "description",
      headerName: t("Description", {
        ns: "Global",
      }),
      flex: 3,
      minWidth: 30,
      sortable: false,
      valueGetter: (value, row) => row.description,
    },
    ...(options.includeStatus
      ? [
          {
            field: "recommendationStatus",
            headerName: t("Status", {
              ns: "PimImportations",
            }),
            flex: 1,
            minWidth: 80,
            sortable: false,
            renderCell: ({ row }) => renderStatusChip(row.recommendationStatus),
          } as GridColDef<DatanormOnlineEntryFragment>,
        ]
      : []),
    {
      field: "filename",
      headerName: t("Name", {
        ns: "Global",
      }),
      flex: 1.5,
      minWidth: 50,
      sortable: false,
      valueGetter: (value, row) => row.filename,
    },
    {
      field: "date",
      headerName: t("Date", {
        ns: "Global",
      }),
      flex: 1,
      minWidth: 30,
      sortable: false,
      valueGetter: (value, row) => row.date,
    },
    {
      field: "dataDate",
      headerName: t("Date of data", {
        ns: "PimImportations",
      }),
      flex: 1,
      minWidth: 30,
      sortable: false,
      valueGetter: (value, row) => row.dataDate,
    },
    {
      field: "actions",
      headerName: t("Actions", {
        ns: "PimImportations",
      }),
      flex: 1,
      minWidth: 30,
      sortable: false,
      renderCell: ({ row }) => {
        const status = statuses[getRowId(row)];
        return !status ? (
          <Button
            size="extra-small"
            variant="contained"
            color="primary"
            disabled={isSyncing}
            onClick={() => handleImport(formikProps.values, row)}
          >
            {t("Import", {
              ns: "PimImportations",
            })}
          </Button>
        ) : status === "loading" ? (
          <CircularProgress color="primary" size="1.25rem" />
        ) : status === "error" ? (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: "center",
            }}
          >
            <Tooltip
              title={t(
                "Error occurred during importation process, please try again later",
                {
                  ns: "PimImportations",
                }
              )}
            >
              <WarningAmberIcon color="warning" fontSize="small" />
            </Tooltip>
            <IconButton
              color="primary"
              size="small"
              disabled={isSyncing}
              onClick={() => handleImport(formikProps.values, row)}
            >
              <Tooltip
                title={t("Retry", {
                  ns: "Global",
                })}
              >
                <ReplayIcon />
              </Tooltip>
            </IconButton>
          </Stack>
        ) : status === "done" ? (
          <CheckIcon color="success" fontSize="small" />
        ) : null;
      },
    },
  ];

  const isConnectedEmpty = entries !== null && entries.length === 0;
  const isConnectedWithEntries = entries !== null && entries.length > 0;

  // all-or-nothing batch sync of the given entries (by row id)
  const handleBatchSync = async (
    values: FormValues,
    rowIdsToSync: string[]
  ) => {
    const { username, clientId, password } = values;
    const idsToSync = new Set(rowIdsToSync);
    const selectedEntries = (entries ?? []).filter(row =>
      idsToSync.has(getRowId(row))
    );
    if (selectedEntries.length === 0) return;
    // mirror the single-entry guard: never send { url: undefined } to the server
    if (selectedEntries.some(row => !row.url)) {
      enqueueSnackbar("Url not found", { variant: "error" });
      return;
    }

    setIsSyncing(true);
    setSyncFailed(false);
    try {
      await addPimImportBatch({
        variables: {
          input: {
            authentication: {
              itekCompanyId,
              username,
              clientId,
              password,
            },
            pimSupplierId: supplierId,
            datanormOnlineEntries: selectedEntries.map(row => ({
              description: row.description,
              dataDate: row.dataDate,
              date: row.date,
              filename: row.filename,
              url: row.url,
            })),
          },
        },
      });
      setStatuses(s => {
        const next = { ...s };
        for (const row of selectedEntries) next[getRowId(row)] = "done";
        return next;
      });
      setSelectedRowIds([]);
      setFailedBatchRowIds([]);
      await refreshEntries(values);
    } catch {
      // surface a single coarse banner — no per-row error states (MEIS-12433)
      setSyncFailed(true);
      // snapshot the attempted set so "Retry Sync" reissues exactly this batch
      setFailedBatchRowIds(rowIdsToSync);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {formikProps => {
        const recommended = entries
          ? sortEntries(entries.filter(e => e.isRecommended))
          : [];
        const more = entries
          ? sortEntries(entries.filter(e => !e.isRecommended))
          : [];
        const recommendedColumns = buildColumns(formikProps, {
          includeStatus: true,
        });
        const moreColumns = buildColumns(formikProps, {
          includeStatus: false,
        });

        // Both sections feed one flat selection (`selectedRowIds`), because the
        // batch sync resolves ids against the full entry list. Each grid only
        // knows its own rows, so its model is scoped to those and a change is
        // merged back without clearing the other section's picks.
        const recommendedIds = new Set(recommended.map(getRowId));
        const moreIds = new Set(more.map(getRowId));

        return (
          <Modal
            dialogProps={{ maxWidth: isConnectedWithEntries ? "md" : "sm" }}
            title={t("Sync via Datanorm Online", {
              ns: "PimImportations",
            })}
            handleClose={handleClose}
            actionButtons={
              entries === null
                ? [
                    {
                      label: t("Cancel", {
                        ns: "Global",
                      }),
                      handleClick: handleClose,
                      buttonProps: { variant: "text" },
                    },
                    {
                      label: t("Submit", {
                        ns: "Global",
                      }),
                      buttonProps: {
                        disabled: !formikProps.dirty || !formikProps.isValid,
                        loading: formikProps.isSubmitting,
                        endIcon: <ArrowForwardIcon />,
                        type: "submit",
                        form: formId,
                      },
                    },
                  ]
                : [
                    {
                      label: t("Done", {
                        ns: "Global",
                      }),
                      handleClick: handleClose,
                      buttonProps: { variant: "text" },
                    },
                    {
                      label: t("Sync selected", {
                        ns: "PimImportations",
                      }),
                      handleClick: () =>
                        handleBatchSync(formikProps.values, selectedRowIds),
                      buttonProps: {
                        disabled: selectedRowIds.length === 0 || isSyncing,
                        loading: isSyncing,
                      },
                    },
                  ]
            }
          >
            {entries === null ? (
              <Form id={formId}>
                <Stack direction="column" spacing={1}>
                  {datanormOnlineInfo.clientIdRequired && (
                    <TextField
                      label={t("Client id", {
                        ns: "PurchaseOrders",
                      })}
                      name="clientId"
                      required
                    />
                  )}
                  {datanormOnlineInfo.usernameRequired && (
                    <TextField
                      label={t("Username", {
                        ns: "PurchaseOrders",
                      })}
                      name="username"
                      required
                    />
                  )}
                  {datanormOnlineInfo.passwordRequired && (
                    <PasswordField
                      name="password"
                      label={t("Password", {
                        ns: "PurchaseOrders",
                      })}
                    />
                  )}
                </Stack>
              </Form>
            ) : isConnectedEmpty ? (
              <Stack
                direction="column"
                spacing={2}
                sx={{
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="h3">
                  {t("Catalogue access not available", {
                    ns: "PimImportations",
                  })}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t(
                    "You're connected to the supplier, but your account isn't authorized to use this catalogue. Contact your company admin or the supplier to enable access.",
                    { ns: "PimImportations" }
                  )}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setEntries(null);
                      setConnectedAccount(null);
                      setStatuses({});
                      // fully reset session state so no stale batch-sync banner,
                      // failed-batch snapshot, row selection or expanded "More"
                      // section leaks into the next connection
                      setSyncFailed(false);
                      setFailedBatchRowIds([]);
                      setSelectedRowIds([]);
                      setMoreExpanded(false);
                    }}
                  >
                    {t("Login with another account", { ns: "PimImportations" })}
                  </Button>
                  <Button variant="text" color="primary" onClick={handleClose}>
                    {t("Upload your file manually", { ns: "PimImportations" })}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack direction="column" spacing={2}>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      "Connection Active via Datanorm Online {version} · Client ID {clientId} · User {user}",
                      {
                        ns: "PimImportations",
                        version: DATANORM_ONLINE_VERSION,
                        clientId: connectedAccount?.clientId || "—",
                        user: connectedAccount?.username || "—",
                      }
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      "The following catalogues are available via DATANORM Online. Select the files to sync with your local system.",
                      { ns: "PimImportations" }
                    )}
                  </Typography>
                </Stack>
                {syncFailed && (
                  <Alert
                    severity="error"
                    onClose={() => setSyncFailed(false)}
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => setSyncFailed(false)}
                        >
                          {t("Try Again Later", {
                            ns: "PimImportations",
                          })}
                        </Button>
                        <Button
                          color="inherit"
                          size="small"
                          startIcon={<ReplayIcon />}
                          disabled={isSyncing}
                          onClick={() =>
                            handleBatchSync(
                              formikProps.values,
                              failedBatchRowIds
                            )
                          }
                        >
                          {t("Retry Sync", {
                            ns: "PimImportations",
                          })}
                        </Button>
                      </Stack>
                    }
                  >
                    <AlertTitle>
                      {t("Sync failed", {
                        ns: "PimImportations",
                      })}
                    </AlertTitle>
                    {t(
                      "Something went wrong while syncing the selected catalogues. No catalogue was imported, please try again.",
                      {
                        ns: "PimImportations",
                      }
                    )}
                  </Alert>
                )}
                <Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {t("Recommended", { ns: "PimImportations" })} (
                    {recommended.length})
                  </Typography>
                  {recommended.length > 0 ? (
                    <DataGrid
                      density="compact"
                      hideFooter={true}
                      getRowId={getRowId}
                      checkboxSelection
                      disableRowSelectionOnClick
                      rowSelectionModel={{
                        type: "include",
                        ids: new Set(
                          selectedRowIds.filter(id => recommendedIds.has(id))
                        ),
                      }}
                      onRowSelectionModelChange={model =>
                        setSelectedRowIds(prev => [
                          ...prev.filter(id => !recommendedIds.has(id)),
                          ...(Array.from(model.ids) as string[]),
                        ])
                      }
                      disableRowSelectionExcludeModel
                      columns={recommendedColumns}
                      rows={recommended}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("No recommended files", { ns: "PimImportations" })}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    onClick={() => setMoreExpanded(v => !v)}
                    sx={{ alignItems: "center", cursor: "pointer", mb: 1 }}
                  >
                    <Typography variant="h4">
                      {t("More", { ns: "PimImportations" })} ({more.length})
                    </Typography>
                    <IconButton
                      size="small"
                      aria-expanded={moreExpanded}
                      aria-label={`${
                        moreExpanded
                          ? t("Collapse", { ns: "Global" })
                          : t("Expand", { ns: "Global" })
                      } ${t("More", { ns: "PimImportations" })}`}
                      onClick={e => {
                        e.stopPropagation();
                        setMoreExpanded(v => !v);
                      }}
                    >
                      {moreExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Stack>
                  <Collapse in={moreExpanded}>
                    {more.length > 0 ? (
                      <DataGrid
                        density="compact"
                        hideFooter={true}
                        getRowId={getRowId}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={{
                          type: "include",
                          ids: new Set(
                            selectedRowIds.filter(id => moreIds.has(id))
                          ),
                        }}
                        onRowSelectionModelChange={model =>
                          setSelectedRowIds(prev => [
                            ...prev.filter(id => !moreIds.has(id)),
                            ...(Array.from(model.ids) as string[]),
                          ])
                        }
                        disableRowSelectionExcludeModel
                        columns={moreColumns}
                        rows={more}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("No additional files", { ns: "PimImportations" })}
                      </Typography>
                    )}
                  </Collapse>
                </Box>
              </Stack>
            )}
          </Modal>
        );
      }}
    </Formik>
  );
}
